import { Check, Minus } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { ScrollReveal } from '#app/components/scroll-reveal.tsx'
import { MarketingButton } from '#app/routes/_marketing+/components/marketing-button.tsx'
import { OneTimePlans, type ShowOneTimePlan } from '#app/services/billing/one-time-plans.ts'
import { type ShowSubscriptionPlan, features, SubscriptionPlans } from '#app/services/billing/subscription-plans.ts'
import { cn } from '#app/utils/misc.js'
import { Title } from './title.tsx'

const usageDisplayOrder = ['chats', 'expedientes', 'transcription'] as const

const cleanUsageLabels: Record<(typeof usageDisplayOrder)[number], string> = {
	chats: 'Consultas jurídicas con IA',
	expedientes: 'Análisis de expedientes legales',
	transcription: 'Transcripciones',
}

const usageResourceKey: Record<(typeof usageDisplayOrder)[number], keyof ShowSubscriptionPlan['resources']> = {
	chats: 'chat',
	expedientes: 'analysis',
	transcription: 'transcription',
}

function UsageRow({ feature, multiplier, dark = false }: { feature: string; multiplier?: number; dark?: boolean }) {
	return (
		<li className="flex items-start justify-between gap-3 text-[15px] leading-relaxed">
			<span className="flex items-start gap-3">
				<Check strokeWidth={2.5} className="mt-0.5 h-4 w-4 shrink-0 text-[#c9a962]" />
				<span className={cn('font-medium', dark ? 'text-white' : 'text-neutral-900')}>{feature}</span>
			</span>
			{multiplier && multiplier > 1 ? (
				<span className="mt-0.5 shrink-0 rounded-full bg-[#c9a962]/15 px-2.5 py-0.5 text-xs font-semibold text-[#c9a962]">
					{multiplier}× más
				</span>
			) : null}
		</li>
	)
}

function FeatureRow({ feature, included, dark = false }: { feature: string; included: boolean; dark?: boolean }) {
	return (
		<li className="flex items-start gap-3 text-[14px] leading-relaxed">
			{included ? (
				<Check strokeWidth={2} className="mt-0.5 h-4 w-4 shrink-0 text-[#c9a962]" />
			) : (
				<Minus strokeWidth={2} className={cn('mt-0.5 h-4 w-4 shrink-0', dark ? 'text-neutral-700' : 'text-neutral-300')} />
			)}
			<span className={cn(included ? (dark ? 'text-neutral-200' : 'text-neutral-700') : 'text-neutral-400')}>
				{feature}
			</span>
		</li>
	)
}

function PlanCard({
	slug,
	plan,
	popular,
	cleanUsage = false,
	comparePlan,
}: {
	slug: keyof typeof SubscriptionPlans
	plan: ShowSubscriptionPlan
	popular?: boolean
	cleanUsage?: boolean
	comparePlan?: ShowSubscriptionPlan
}) {
	const dark = plan.featured
	const showBadge = popular ?? plan.featured

	return (
		<article
			className={cn(
				'relative flex h-full flex-col rounded-3xl p-8 lg:p-10',
				dark ? 'bg-neutral-950 shadow-2xl shadow-neutral-900/20' : 'border border-neutral-200 bg-white',
			)}
		>
			{showBadge && (
				<span className="absolute -top-3 left-8 rounded-full bg-[#c9a962] px-4 py-1 text-xs font-medium text-neutral-950">
					Más popular
				</span>
			)}

			<div className="space-y-2">
				<h3 className={cn('text-xl font-medium tracking-tight', dark ? 'text-white' : 'text-neutral-950')}>
					{plan.title}
				</h3>
				<p className={cn('text-sm leading-relaxed', dark ? 'text-neutral-400' : 'text-neutral-500')}>{plan.subtitle}</p>
			</div>

			<div className="mt-8">
				<div className="flex items-end gap-2.5">
					<Title component="span" className={cn('text-5xl', dark ? 'text-white' : 'text-neutral-950')}>
						${plan.price}
					</Title>
					{plan.oldPrice && <span className="mb-1.5 text-lg text-neutral-400 line-through">${plan.oldPrice}</span>}
				</div>
				<p className={cn('mt-2 text-sm', dark ? 'text-neutral-400' : 'text-neutral-500')}>+ IVA {plan.period}</p>
			</div>

			<ul className="mt-8 space-y-3">
				{usageDisplayOrder.map(key => {
					const value = plan.usage[key]
					if (!value) return null
					if (!cleanUsage) {
						return <UsageRow key={key} feature={value} dark={dark} />
					}
					const resourceKey = usageResourceKey[key]
					const multiplier = comparePlan
						? Math.round(plan.resources[resourceKey] / comparePlan.resources[resourceKey])
						: undefined
					return <UsageRow key={key} feature={cleanUsageLabels[key]} multiplier={multiplier} dark={dark} />
				})}
			</ul>

			<div className={cn('mt-8 flex-1 border-t pt-8', dark ? 'border-neutral-800' : 'border-neutral-100')}>
				<p
					className={cn(
						'text-xs font-medium uppercase tracking-widest',
						dark ? 'text-neutral-500' : 'text-neutral-400',
					)}
				>
					Características incluidas
				</p>
				<ul className="mt-5 space-y-3">
					{features.map(feature => (
						<FeatureRow
							key={feature.name}
							feature={feature.name}
							included={feature.includedIn.includes(slug)}
							dark={dark}
						/>
					))}
				</ul>
			</div>

			<MarketingButton variant={dark ? 'inverted' : 'primary'} className="mt-8 w-full" asChild>
				<Link to="/signup">{plan.cta}</Link>
			</MarketingButton>
		</article>
	)
}

function EnterprisePlanCard() {
	return (
		<article className="relative flex h-full flex-col rounded-3xl border border-neutral-200 bg-white p-8 lg:p-10">
			<div className="space-y-2">
				<h3 className="text-xl font-medium tracking-tight text-neutral-950">Empresarial</h3>
				<p className="text-sm leading-relaxed text-neutral-500">
					Para bufetes de abogados y despachos. Contáctenos para crear un plan ajustado a sus necesidades.
				</p>
			</div>

			<div className="mt-8">
				<Title component="span" className="text-5xl text-neutral-950">
					A medida
				</Title>
				<p className="mt-2 text-sm text-neutral-500">Plan ajustado a su organización</p>
			</div>

			<ul className="mt-8 space-y-3">
				<UsageRow feature="Cantidad de chats personalizados" />
				<UsageRow feature="Cantidad de análisis personalizados" />
				<UsageRow feature="Cantidad de transcripciones personalizadas" />
			</ul>

			<div className="mt-8 flex-1 border-t border-neutral-100 pt-8">
				<p className="text-xs font-medium uppercase tracking-widest text-neutral-400">Características incluidas</p>
				<ul className="mt-5 space-y-3">
					<FeatureRow feature="Todas las funcionalidades del plan Avanzado" included={true} />
					<FeatureRow feature="Centro de gestión de usuarios" included={true} />
					<FeatureRow feature="Soporte prioritario" included={true} />
					<FeatureRow feature="Funcionalidades a la medida" included={true} />
					<FeatureRow feature="Descuentos por cantidad" included={true} />
				</ul>
			</div>

			<MarketingButton variant="primary" className="mt-8 w-full" asChild>
				<Link to="/registro-empresa">Contáctanos</Link>
			</MarketingButton>
		</article>
	)
}

function AddonPackageCard({ addon }: { addon: ShowOneTimePlan }) {
	const { name, description, price } = addon

	return (
		<article className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-6">
			<h4 className="text-lg font-medium tracking-tight text-neutral-950">{name}</h4>
			<p className="flex-1 text-sm leading-relaxed text-neutral-600">{description}</p>
			<Title component="p" className="text-2xl text-neutral-950">
				${price}
			</Title>
		</article>
	)
}

export function PlansComponent() {
	const [planType, setPlanType] = useState<'normal' | 'student'>('normal')

	return (
		<section className="w-full bg-neutral-50 py-32 md:py-40" id="planes">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<ScrollReveal className="mx-auto max-w-3xl text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">Planes</p>
					<Title component="h2" className="mt-6 text-4xl md:text-5xl">
						Planes diseñados para profesionales legales
					</Title>
					<p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-neutral-600">
						Selecciona el plan que mejor se adapte a tus necesidades.
					</p>

					<div className="mt-12 flex justify-center">
						<div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
							<button
								className={cn(
									'rounded-full px-6 py-2 text-sm font-medium transition-colors',
									planType === 'normal' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900',
								)}
								onClick={() => setPlanType('normal')}
							>
								Abogados
							</button>
							<button
								className={cn(
									'rounded-full px-6 py-2 text-sm font-medium transition-colors',
									planType === 'student' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900',
								)}
								onClick={() => setPlanType('student')}
							>
								Estudiantes
							</button>
						</div>
					</div>
				</ScrollReveal>

				{planType === 'normal' ? (
					<div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3 lg:gap-8">
						<ScrollReveal>
							<PlanCard plan={SubscriptionPlans.basic!} slug="basic" cleanUsage />
						</ScrollReveal>
						<ScrollReveal delay={100}>
							<PlanCard
								plan={SubscriptionPlans.advanced!}
								slug="advanced"
								cleanUsage
								comparePlan={SubscriptionPlans.basic!}
							/>
						</ScrollReveal>
						<ScrollReveal delay={200}>
							<EnterprisePlanCard />
						</ScrollReveal>
					</div>
				) : (
					<div className="mt-16 flex justify-center">
						<ScrollReveal className="w-full max-w-md">
							<PlanCard plan={SubscriptionPlans.student!} slug="student" popular={false} />
						</ScrollReveal>
					</div>
				)}

				<ScrollReveal className="mt-24 border-t border-neutral-200 pt-16">
					<div className="max-w-2xl">
						<Title component="h3" className="text-2xl md:text-3xl">
							Paquetes adicionales
						</Title>
						<p className="mt-3 text-base leading-relaxed text-neutral-600">
							¿Necesitas más consultas o análisis? Amplía tu plan en cualquier momento.
						</p>
					</div>

					<div className="mt-10 grid gap-6 md:grid-cols-3">
						{Object.entries(OneTimePlans).map(([slug, plan]) => (
							<AddonPackageCard key={slug} addon={plan} />
						))}
					</div>

					<div className="mt-12 flex flex-col justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-8 sm:flex-row sm:items-center">
						<p className="text-base text-neutral-700">¿Necesitas un plan personalizado para tu organización?</p>
						<MarketingButton variant="primary" className="shrink-0" asChild>
							<Link to="mailto:luis@iuspro.com">Contacta con nosotros</Link>
						</MarketingButton>
					</div>
				</ScrollReveal>
			</div>
		</section>
	)
}
